import React, { memo } from 'react';
import { Box, Stack, SvgIconProps, Tooltip, Typography, alpha } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  CallSplitRounded, CalendarViewWeekOutlined, MeetingRoomOutlined, ScienceOutlined, ComputerOutlined, TheaterComedyOutlined,
  LocalLibraryOutlined, SportsSoccerOutlined, PlaceOutlined, ScheduleOutlined, WbSunnyOutlined, WbTwilightOutlined, ViewDayOutlined,
  PersonOffOutlined,
} from '@mui/icons-material';
import type { LessonRules, PreferredTime } from '../../../services/timetableService';
import { ToneChip } from '../../common/StatusChip';
import PersonAvatar from '../../common/PersonAvatar';
import { PickerOption, RulePicker, Stepper } from './LightFields';
import { ROOM_TYPE_LABELS, SPECIAL_ROOM_TYPES, fixLink, subjectHue } from '../timetableUtils';

/** One curriculum entry of a class: the subject, who teaches it in each section, and its timetable rules. */
export interface LessonRowData {
  curriculumId: string;
  subjectId: string;
  subjectName: string;
  /** Set when the entry belongs to one section only (e.g. Physics for the Science section). */
  onlySection: string | null;
  /** Who teaches it in each section; `share` is set when the subject is split between teachers there. */
  teachers: { section: string | null; name: string | null; share: number | null }[];
  count: number;
  doubles: number;
  maxPerDay: number | null;
  roomType: string | null;
  preferredTime: PreferredTime;
}

export const LESSON_GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: 'minmax(120px, 1fr) minmax(150px, 1.25fr) 92px 92px 128px 136px 108px' },
  columnGap: 1.25,
  rowGap: 1,
  alignItems: 'center',
} as const;

/**
 * One chip per teacher, naming the sections they take ("A, B · Sana Iqbal"),
 * and one chip per section where the subject is split ("Sci · Akbar 3, Faiz 2").
 */
const teacherChips = (teachers: LessonRowData['teachers']): { key: string; label: string; name: string | null; sections: string; missing: boolean; split: boolean }[] => {
  const whole = new Map<string | null, string[]>();
  const split = new Map<string, string[]>();
  teachers.forEach((t) => {
    if (t.share !== null) {
      const sec = t.section ?? '';
      split.set(sec, [...(split.get(sec) ?? []), `${t.name} ${t.share}`]);
      return;
    }
    const list = whole.get(t.name) ?? [];
    if (t.section) list.push(t.section);
    whole.set(t.name, list);
  });
  return [
    ...Array.from(whole.entries()).map(([name, sections]) => ({
      key: `t:${name}`, missing: !name, split: false, name, sections: sections.sort().join(', '),
      label: name ?? 'No teacher',
    })),
    ...Array.from(split.entries()).sort().map(([sec, parts]) => ({
      key: `s:${sec}`, missing: false, split: true, name: null, sections: sec, label: parts.join(', '),
    })),
  ];
};

const ROOM_ICONS: Record<string, React.ReactElement<SvgIconProps>> = {
  lab: <ScienceOutlined />, computer_lab: <ComputerOutlined />, auditorium: <TheaterComedyOutlined />,
  library: <LocalLibraryOutlined />, sports: <SportsSoccerOutlined />, other: <PlaceOutlined />,
};
const ROOM_OPTIONS: PickerOption<string | null>[] = [
  { value: null, label: 'Own classroom', icon: <MeetingRoomOutlined /> },
  ...SPECIAL_ROOM_TYPES.map((t) => ({ value: t, label: ROOM_TYPE_LABELS[t], icon: ROOM_ICONS[t] })),
];
const TIME_OPTIONS: PickerOption<PreferredTime>[] = [
  { value: 'any', label: 'Any time', icon: <ScheduleOutlined /> },
  { value: 'morning', label: 'Morning', icon: <WbSunnyOutlined /> },
  { value: 'afternoon', label: 'Afternoon', icon: <WbTwilightOutlined /> },
];
const dailyOptions = (doubles: number): PickerOption<number | null>[] => [
  { value: null, label: 'Spread evenly', icon: <CalendarViewWeekOutlined /> },
  ...[1, 2, 3, 4].map((n) => ({ value: n, label: `At most ${n} a day`, icon: <ViewDayOutlined />, disabled: doubles > 0 && n < 2 })),
];

/** The column titles, in the rows' grid. */
export const LessonHeader = memo(() => (
  <Box sx={{ ...LESSON_GRID, display: { xs: 'none', lg: 'grid' }, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    {['Subject', 'Taught by', 'Periods', 'Doubles', 'Daily limit', 'Room', 'Time of day'].map((h) => (
      <Typography key={h} variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem' }}>{h}</Typography>
    ))}
  </Box>
));
LessonHeader.displayName = 'LessonHeader';

interface LessonRowProps {
  row: LessonRowData;
  sessionId: string;
  canWrite: boolean;
  onChange: (curriculumId: string, rules: LessonRules) => void;
}

/** Same values, same row: a reload hands every row a new object, but only changed rows redraw. */
const sameRow = (a: LessonRowProps, b: LessonRowProps) =>
  a.sessionId === b.sessionId && a.canWrite === b.canWrite && a.onChange === b.onChange
  && JSON.stringify(a.row) === JSON.stringify(b.row);

const LessonRow = memo<LessonRowProps>(({ row, sessionId, canWrite, onChange }) => {
  const set = (rules: LessonRules) => onChange(row.curriculumId, rules);
  const teachingLoads = fixLink('teaching_loads', sessionId);
  const ro = !canWrite;
  const hue = Math.round(subjectHue(row.subjectId));
  const maxDoubles = Math.floor(row.count / 2);

  return (
    <Box
      sx={{
        ...LESSON_GRID, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 }, transition: 'background-color 120ms', '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '3px', flexShrink: 0, bgcolor: `hsl(${hue}, 62%, 52%)` }} />
        <Typography variant="body2" fontWeight={650} noWrap>{row.subjectName}</Typography>
        {row.onlySection && <ToneChip label={`${row.onlySection} only`} tone="neutral" />}
      </Stack>

      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center">
        {teacherChips(row.teachers).map((t) => {
          if (t.missing) {
            return (
              <Tooltip key={t.key} title="Assign a teacher in Teaching Loads">
                <Box
                  component={RouterLink} to={teachingLoads.to}
                  sx={(theme) => ({
                    display: 'inline-flex', alignItems: 'center', gap: 0.75, pl: 0.5, pr: 1.25, height: 28, borderRadius: '999px', textDecoration: 'none',
                    color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08), border: '1px dashed', borderColor: alpha(theme.palette.error.main, 0.45),
                  })}
                >
                  <PersonOffOutlined sx={{ fontSize: 18 }} />
                  <Typography variant="caption" fontWeight={600}>{t.sections ? `${t.sections} · ` : ''}No teacher</Typography>
                </Box>
              </Tooltip>
            );
          }
          const chip = (
            <Box
              key={t.key}
              sx={(theme) => ({
                display: 'inline-flex', alignItems: 'center', gap: 0.75, pl: t.split ? 1 : 0.4, pr: 1.25, height: 28, maxWidth: 280, borderRadius: '999px',
                border: '1px solid', borderColor: t.split ? alpha(theme.palette.primary.main, 0.3) : 'divider',
                bgcolor: t.split ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
              })}
            >
              {t.split ? <CallSplitRounded sx={{ fontSize: 16, color: 'primary.main' }} /> : <PersonAvatar name={t.name} size={20} />}
              <Typography variant="caption" noWrap sx={{ fontWeight: 600, color: 'text.primary' }}>
                {t.sections && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>{t.sections} · </Box>}
                {t.label}
              </Typography>
            </Box>
          );
          return t.split ? <Tooltip key={t.key} title="Periods split between teachers. Change the split in Teaching Loads.">{chip}</Tooltip> : chip;
        })}
      </Stack>

      <Stepper
        label="Periods a week" value={row.count} min={1} max={50} disabled={ro}
        rangeHint="Periods a week must be between 1 and 50."
        onCommit={(v) => set({ periodsPerWeek: v })}
      />
      <Stepper
        label="Double periods" value={row.doubles} min={0} max={maxDoubles} disabled={ro}
        rangeHint={`${row.count} periods a week allow at most ${maxDoubles} double period${maxDoubles === 1 ? '' : 's'}.`}
        onCommit={(v) => set({ doubles: v })}
      />
      <Box><RulePicker variant="pill" label="Daily limit" disabled={ro} value={row.maxPerDay} options={dailyOptions(row.doubles)} onChange={(v) => set({ maxPerDay: v })} /></Box>
      <Box><RulePicker variant="pill" label="Room" disabled={ro} value={row.roomType} options={ROOM_OPTIONS} onChange={(v) => set({ roomType: v })} /></Box>
      <Box><RulePicker variant="pill" label="Time of day" disabled={ro} value={row.preferredTime} options={TIME_OPTIONS} onChange={(v) => set({ preferredTime: v })} /></Box>
    </Box>
  );
}, sameRow);

LessonRow.displayName = 'LessonRow';

export default LessonRow;
